import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button, Form, Select, Row, Col, Radio, Modal, Spin } from 'antd';
import Layout from 'components/Layout';
import Meta from 'components/Meta';
import Image from 'components/Image';
import ScrollDown from 'components/ScrollDown';
import SectionContact from 'components/SectionContact';
import FloatingText from 'components/FloatingText';
import styled from 'styled-components';
import { CareerContainer, ImageBanner } from 'styles/careers';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';

const { Option, OptGroup } = Select;
const StyledRow = styled(Row)`
  .ant-col .ant-form-item-control {
    margin-right: 25px;
  }
  .ant-select {
    width: 278px !important;
    height: 50px !important;
    .ant-select-selector {
      height: 100% !important;
    }
    .ant-select-selection-placeholder {
      color: #5f5f5f;
      font-family: Vollkorn;
      font-size: 20px;
      letter-spacing: -0.15px;
      line-height: 28px;
      padding-top: 10px !important;
      padding-left: 19px !important;
    }
    .ant-select-selection-item {
      font-family: Vollkorn;
      font-size: 20px;
      letter-spacing: -0.15px;
      line-height: 28px;
      padding-top: 10px !important;
      padding-left: 19px !important;
    }
  }
  .ant-btn.pink-btn {
    height: 46px !important;
    min-width: 110px !important;
    &:active {
      background-color: #ff4552 !important;
      color: #fff !important;
    }
    &:focus {
      background-color: #ff4552 !important;
      color: #fff !important;
    }
  }
  .reset-btn {
    height: 46px !important;
    outline: none !important;
    border: none;
    box-shadow: none;
    color: #0a090b !important;
    font-weight: 600 !important;
    font-family: 'Be Vietnam Pro' !important;
    font-size: 16px !important;
  }
  @media screen and (max-width: 450px) {
    .ant-select {
      width: 100% !important;
    }
  }

  @media (max-width: 1025px) and (min-width: 768px) {
    .ant-select {
      width: 206px !important;
    }
  }

  display: flex !important;
  flex-flow: row wrap;
  .ant-select-dropdown {
    svg {
      display: none;
    }
  }
`;

const StyledRowLabel = styled(Row)`
  .ant-col {
    color: #5f5f5f;
    width: 278px;
    font-family: Vollkorn;
    font-size: 16px;
    letter-spacing: -0.12px;
    line-height: 22px;
    margin-right: 25px;
  }
  .ant-col:last-child {
    width: auto;
    height: 39px !important;
  }
`;

const StyledCol = styled(Col)`
  .ant-form-item-control-input {
    min-height: 50px;
  }
  @media screen and (max-width: 450px) {
    width: 100% !important;
  }
`;

const CareerPage = ({ path }) => {
  const [jobs, setJobs] = useState([]);
  const [form] = Form.useForm();
  const [locationList, setLocationList] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [jobLink, setJobLink] = useState('');
  const [enableLoader, setEnableLoader] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [isFocus, setIsFocus] = useState(false);

  function handleChange(value) {
    if (value && value.length && value.includes('all')) {
      if (value.length === locationList.length + 1) {
        return setSelectedLocation([]);
      }
      return setSelectedLocation(locationList.map((loc) => loc.value));
    }
    return setSelectedLocation(value);
  }

  // useEffect(() => {
  //   axios
  //     .get(
  //       'https://api.resumatorapi.com/v1/jobs?apikey=lgkKYRuENQ9tzOlNjRWhqDgeReEZ6uHL',
  //     )
  //     .then(({ data }) => {
  //       setJobs(data);
  //     });
  // }, []);

  useEffect(() => {
    let locations = uniq(jobs.map((job) => job.location && job.location.name));
    locations = locations.filter((obj) => obj !== '' && obj !== undefined);
    const locationArr = [];
    locations.map((location) =>
      locationArr.push({
        value: location,
        label: location,
        city: location.split(',')[0],
        state: location.split(',')[1],
        country: location.split(',')[location.split(',').length - 1],
      }),
    );
    setLocationList(locationArr);
    setFilteredJobs(sortBy(jobs, (job) => job.title));
  }, [jobs]);

  useEffect(() => {
    axios
      .get('https://hire.api.auzmor.com/api/v1/careers/atc')
      .then(({ data }) => {
        setJobs(data.filter((i) => i.status === 'Open'));
      });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setEnableLoader(false), 2000);
    return () => clearTimeout(timer);
  }, [jobLink]);

  const submitForm = async () => {
    try {
      const values = await form.validateFields();
      const filteredJobList = selectedLocation.length
        ? jobs.filter(
            (job) =>
              job.location && selectedLocation.includes(job.location.name),
          )
        : jobs;
      setFilteredJobs(sortBy(filteredJobList, (job) => job.title));
    } catch (errorInfo) {
      console.log('Failed:', errorInfo);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setSelectedLocation([]);
    setFilteredJobs(sortBy(jobs, (job) => job.title));
  };

  const openJobLinkInNewTab = (jobLink) => {
    window.open(jobLink, '_blank');
  };

  return (
    <>
      <Meta
        title="ATC Careers | IT Staffing, Software Development, Project Management Positions"
        description="We're Hiring! An exciting future awaits you at ATC. Work with us to improve your career growth. Browse for more career opportunities."
      />
      <Layout path={path}>
        <CareerContainer>
          <section style={{ overflow: 'hidden' }}>
            <div className="career-hero-main">
              <div className="container">
                <div className="inner-hero-area career-hero">
                  <ScrollDown />
                  <div className="row">
                    <div className="col-lg-7 col-md-10">
                      <h1 className="white">
                        Join us
                        <span className="pink">.</span>
                      </h1>
                      <div className="inner-hero-content">
                        <p className="white">
                          Join our team of passionate people aligned toward the
                          goal of helping our clients win. Exciting challenges
                          lie ahead. Explore various career opportunities with
                          ATC, and grow with us.
                        </p>
                        <a
                          href="#open-positions"
                          onClick={(e) => {
                            e.preventDefault();
                            if (typeof window !== 'undefined')
                              window.scrollb.scrollIntoView(
                                document.getElementById('open-positions'),
                              );
                          }}
                          className="pink-btn"
                        >
                          <span>See Open Positions</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <ImageBanner>
                <Image src="careers/atc-team.jpg" alt="atc team" />
              </ImageBanner>
            </div>
          </section>

          <section className="white-bg-section">
            <div className="career-images-area">
              <div className="container">
                <div className="pos-rel">
                  <div className="row">
                    <div className="col-md-5 offset-md-1">
                      <h2 className="big-heading">
                        Grow with
                        <span className="pink"> us.</span>
                      </h2>
                      <h3 className="career-hashtag-txt d-block d-md-none">
                        #better
                        <br />
                        together
                      </h3>
                      <div className="career-img career-first-img">
                        <FloatingText
                          text="Passionate people with a singular goal of helping our clients win"
                          dark
                          style={{ transform: 'translateX(-70px)' }}
                        />
                        <Image src="careers/atc-careers-2.jpg" alt="team" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h3 className="career-hashtag-txt d-none d-md-block">
                        #better
                        <br />
                        together
                      </h3>
                      <Image
                        type="background"
                        tag="div"
                        src="careers/atc-careers-1.jpg"
                        className="career-extended-img"
                      />
                      <div className="career-img">
                        <Image
                          src="careers/atc-careers-3.jpg"
                          className="career-extended-img"
                          alt="team"
                          style={{ maxWidth: 430 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="container">
              <div className="career-why-us">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="single-why-us">
                      <h2 className="big-heading white-txt">
                        Why join us
                        <span className="pink">?</span>
                      </h2>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <p className="white-txt">
                      Our principles guide everything we do. We bring
                      passionate, like-minded people together with a singular
                      goal of helping our clients win by providing unparalleled
                      service and technology solutions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="career-section-2">
              <div className="row no-gutters">
                <div className="col-md-4 no-gutters">
                  <Image
                    type="background"
                    tag="div"
                    src="careers/atc-fearlessness.jpg"
                    className="single-career-feature darken-image"
                  >
                    <h3>Fearlessness</h3>
                    <p>
                      We embrace change and innovation in order to deliver the
                      best solutions.
                    </p>
                  </Image>
                </div>
                <div className="col-md-4 no-gutters">
                  <div
                    className="single-career-feature"
                    style={{ backgroundColor: '#E53B47' }}
                  >
                    <h3>Humility</h3>
                    <p>
                      We are committed to a culture of teamwork and
                      collaboration.
                    </p>
                  </div>
                </div>
                <div className="col-md-4 no-gutters">
                  <div
                    className="single-career-feature"
                    style={{ backgroundColor: '#F5515C' }}
                  >
                    <h3>Inclusiveness</h3>
                    <p>
                      We respect people, value diversity and are committed to
                      equality.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="white-bg-section d-none d-md-block">
            <div className="career-section-3">
              <div className="row no-gutters">
                <div className="col-md-6 no-gutters">
                  <div className="section-3-text">
                    <h3>Learning</h3>
                    <p>We believe the future belongs to the learners.</p>
                  </div>
                </div>
                <div className="col-md-6 no-gutters">
                  <div
                    className="section-3-text"
                    style={{ backgroundColor: '#F93B48' }}
                  >
                    <h3 className="white-txt">Customers</h3>
                    <p className="white-txt">
                      We exist to meet the needs of our customers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Image type="background" tag="section" src="careers/we-care.jpg">
            <div className="we-care-main">
              <div className="container">
                <div className="we-care-area">
                  <h2 className="big-heading white-txt">
                    We
                    <br className="d-none d-md-block" />
                    care
                    <span className="pink">.</span>
                  </h2>

                  <div className="we-care-block">
                    <div className="row">
                      <div className="col-lg-9">
                        <div className="row">
                          <div className="col-md-4 col-6">
                            <div className="we-care-single">
                              <h3>Medical Insurance</h3>
                              <p>Private medical care</p>
                            </div>
                          </div>
                          <div className="col-md-4 col-6">
                            <div className="we-care-single">
                              <h3>Holiday Pay</h3>
                              <p>Paid Holidays Off</p>
                            </div>
                          </div>
                          <div className="col-md-4 col-6">
                            <div className="we-care-single">
                              <h3>401k</h3>
                              <p>Retirement Planning</p>
                            </div>
                          </div>
                          <div className="col-md-4 col-6">
                            <div className="we-care-single">
                              <h3>Dental Insurance</h3>
                              <p>Private Dental Care</p>
                            </div>
                          </div>
                          <div className="col-md-4 col-6">
                            <div className="we-care-single">
                              <h3>Vision Insurance</h3>
                              <p>Private Vision Care</p>
                            </div>
                          </div>
                          <div className="col-md-4 col-6">
                            <div className="we-care-single">
                              <h3>Accident Insurance</h3>
                              <p>Private Accident Coverage</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Image>

          <section className="white-bg-section" id="open-positions">
            <div className="container">
              <div className="open-position-main">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h2 className="big-heading">
                      Open Roles
                      <span className="pink">.</span>
                    </h2>
                  </div>
                </div>
                <div className="form-container" style={{ marginTop: 25 }}>
                  <Form form={form}>
                    <StyledRow>
                      <StyledCol style={{ width: 325, marginRight: 20 }}>
                        <StyledRowLabel>
                          <Col>Location</Col>
                        </StyledRowLabel>
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ width: '100%' }}
                          placeholder="Select Location"
                          onChange={handleChange}
                          value={selectedLocation}
                          labelInValue={false}
                          onSearch={() => setIsFocus(true)}
                          onBlur={() => setIsFocus(false)}
                          tagRender={(item) =>
                            selectedLocation[0] === item.value && !isFocus ? (
                              <span className="location-placeholder">
                                Location(
                                {selectedLocation.length})
                              </span>
                            ) : (
                              ''
                            )
                          }
                          showArrow
                          dropdownClassName="location-multiselect"
                        >
                          <Option key="all">
                            <input
                              type="checkbox"
                              name="loc"
                              value="all"
                              checked={
                                selectedLocation.length === locationList.length
                              }
                            />
                            <span className="location-name"> Select All</span>{' '}
                          </Option>
                          {uniq(locationList.map((loc) => loc.country)).map(
                            (country) => (
                              <OptGroup label={country}>
                                {locationList.map((item) => {
                                  if (item.country === country) {
                                    return (
                                      <>
                                        <Option key={item.value}>
                                          <input
                                            type="checkbox"
                                            name="loc"
                                            value={item.value}
                                            checked={selectedLocation.includes(
                                              item.value,
                                            )}
                                          />{' '}
                                          <span className="location-name">
                                            {item.label}
                                          </span>{' '}
                                        </Option>
                                      </>
                                    );
                                  }
                                })}
                              </OptGroup>
                            ),
                          )}
                        </Select>
                        <br />
                      </StyledCol>
                      <StyledCol className="btn-col">
                        <StyledRowLabel>
                          <Col></Col>
                        </StyledRowLabel>
                        <Button
                          htmlType="submit"
                          className="pink-btn"
                          onClick={submitForm}
                        >
                          Apply
                        </Button>
                        <Button onClick={resetForm} className="reset-btn">
                          Reset
                        </Button>
                      </StyledCol>
                    </StyledRow>
                  </Form>
                </div>
                <div className="jobs-count">
                  Showing
                  {filteredJobs.length}{' '}
                </div>
                <div className="accordion-body">
                  {jobs.length > 0 &&
                    filteredJobs
                      .filter((i) => i.status === 'Open')
                      .map((i) => (
                        <div key={i.uuid} className="single-open-position">
                          <button
                            onClick={() =>
                              openJobLinkInNewTab(
                                `https://hire.auzmor.com/atc/careers/${i.uuid}`,
                              )
                            }
                          >
                            {i.title}
                          </button>
                          <p>
                            {`${
                              i.isRemote
                                ? 'Remote'
                                : i.location
                                ? i.location.name
                                : ''
                            }`}
                          </p>
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </section>

          <SectionContact
            title="Apply to join our team!"
            cta="APPLY"
            to="#open-positions"
            isAnchorLink
          />
        </CareerContainer>
      </Layout>
    </>
  );
};

CareerPage.propTypes = {
  path: PropTypes.string.isRequired,
};

export default CareerPage;
